import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Send, Database, Clock, CheckCircle, AlertCircle, Loader, ChevronDown, Search, Globe } from 'lucide-react';
import './App.css';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [schemaDirectories, setSchemaDirectories] = useState([]);
  const [selectedSchemaDir, setSelectedSchemaDir] = useState('ett-device');
  const [healthStatus, setHealthStatus] = useState(null);
  const messagesContainerRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [schemaSearchTerm, setSchemaSearchTerm] = useState('');

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100; // 100px threshold
    setShouldAutoScroll(isNearBottom);
    setShowScrollButton(!isNearBottom);
  };

  useEffect(() => {
    if (shouldAutoScroll) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages, shouldAutoScroll]);

  useEffect(() => {
    // Load schemas and health status on component mount
    loadSchemaDirectories();
    checkHealth();
  }, []);

  useEffect(() => {
    // Load schemas when selectedSchemaDir changes
    if (selectedSchemaDir) {
      loadSchemas(selectedSchemaDir);
    }
  }, [selectedSchemaDir]);

  useEffect(() => {
    // Add scroll event listener
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const checkHealth = async () => {
    try {
      const response = await axios.get('/health');
      setHealthStatus(response.data);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus({ status: 'error', available_schemas: 0 });
    }
  };

  const loadSchemaDirectories = async () => {
    try {
      const response = await axios.get('/schema-directories');
      setSchemaDirectories(response.data);
      // Set default to ett-device if available, otherwise use first available
      if (response.data.length > 0) {
        const ettDevice = response.data.find(dir => dir.name === 'ett-device');
        if (ettDevice) {
          setSelectedSchemaDir('ett-device');
        } else {
          setSelectedSchemaDir(response.data[0].name);
        }
      }
    } catch (error) {
      console.error('Failed to load schema directories:', error);
    }
  };

  const loadSchemas = async (schemaDir = selectedSchemaDir) => {
    try {
      const response = await axios.get(`/schemas?schema_dir=${schemaDir}`);
      setSchemas(response.data);
    } catch (error) {
      console.error('Failed to load schemas:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShouldAutoScroll(true); // Auto-scroll when user sends a message

    try {
      // Use the execute-query endpoint to convert natural language to SQL and execute it
      const response = await axios.post('/execute-query', {
        query: inputValue,
        schema_dir: selectedSchemaDir
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.data.sql_query,
        naturalLanguage: response.data.natural_language,
        results: response.data.results,
        rowCount: response.data.row_count,
        columns: response.data.columns,
        executionTime: response.data.execution_time,
        timestamp: response.data.timestamp,
        status: response.data.status,
        error: response.data.error
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: error.response?.data?.detail || 'Failed to execute query',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Database className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
              <header className="bg-white shadow-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">Andorean Storage</h1>
                <p className="text-sm text-gray-600 mt-1">Natural language to SQL query processor</p>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-xl">
                  <div className={`w-3 h-3 rounded-full ${healthStatus?.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm font-medium text-gray-700">
                    {healthStatus?.available_schemas || 0} schemas
                  </span>
                </div>
                <div className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-xl">
                  <div className={`w-3 h-3 rounded-full ${healthStatus?.database_connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm font-medium text-gray-700">
                    {healthStatus?.database_connected ? 'DB Connected' : 'DB Disconnected'}
                  </span>
                </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Schema Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 h-[80vh] overflow-hidden flex flex-col">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex-shrink-0">Available Data</h2>
              
              {/* Schema Directory Selector */}
              <div className="mb-6 flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">Database</label>
                <div className="relative">
                  <select
                    value={selectedSchemaDir}
                    onChange={(e) => setSelectedSchemaDir(e.target.value)}
                    className="w-full pl-3 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm bg-gray-50 appearance-none cursor-pointer"
                  >
                    {schemaDirectories.map((dir) => (
                      <option key={dir.name} value={dir.name}>
                        {dir.name} ({dir.schema_count} schemas)
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              {/* Search Input */}
              <div className="relative mb-6 flex-shrink-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search schemas..."
                  value={schemaSearchTerm}
                  onChange={(e) => setSchemaSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm bg-gray-50"
                />
              </div>
              
              <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                {(() => {
                  const filteredSchemas = schemas.filter(schema => 
                    schema.name.toLowerCase().includes(schemaSearchTerm.toLowerCase()) ||
                    schema.description.toLowerCase().includes(schemaSearchTerm.toLowerCase())
                  );
                  
                  if (filteredSchemas.length === 0 && schemaSearchTerm) {
                    return (
                      <div className="text-center py-12">
                        <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-sm text-gray-500">No schemas found matching "{schemaSearchTerm}"</p>
                      </div>
                    );
                  }
                  
                  return filteredSchemas.map((schema) => (
                    <div key={schema.name} className="group bg-white border border-gray-100 rounded-xl p-5 hover:border-blue-200 hover:shadow-lg transition-all duration-300 cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-gray-800 text-base truncate group-hover:text-blue-600 transition-colors">
                          {schema.name}
                        </h3>
                        <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                          {schema.fields.length} fields
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed group-hover:text-gray-700 transition-colors">
                        {schema.description}
                      </p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="lg:col-span-3">
                         <div className="bg-white rounded-lg shadow-lg border border-gray-200">
               {/* Messages */}
               <div 
                 ref={messagesContainerRef}
                 className="h-[67vh] rounded-lg overflow-y-auto p-8 space-y-6 relative bg-white"
               >
                {messages.length === 0 && (
                                     <div className="text-center py-16">
                     <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                       <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                       </svg>
                     </div>
                     <h3 className="text-2xl font-bold text-gray-800 mb-3">Welcome to Andorean Storage</h3>
                     <p className="text-gray-600 mb-6 text-lg">Ask me anything about your mining operations data!</p>
                     <div className="text-sm text-gray-500 max-w-md mx-auto">
                       <p className="font-medium mb-3">Try asking:</p>
                       <ul className="space-y-2 text-left">
                         <li className="flex items-center">
                           <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                           "Show me all race logs for CAT 777E-31 today"
                         </li>
                         <li className="flex items-center">
                           <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                           "What's the efficiency of excavator EX-01 this week?"
                         </li>
                         <li className="flex items-center">
                           <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                           "Display zone entry logs for the last 7 days"
                         </li>
                       </ul>
                     </div>
                   </div>
                )}
                
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                                             className={`max-w-4xl rounded-2xl px-6 py-4 shadow-sm ${
                         message.type === 'user'
                           ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                           : message.type === 'error'
                           ? 'bg-red-50 border border-red-200 text-red-800'
                           : 'bg-gray-50 border border-gray-100 text-gray-900'
                       }`}
                    >
                      {message.type === 'user' ? (
                        <p>{message.content}</p>
                      ) : message.type === 'error' ? (
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-4 h-4" />
                          <p>{message.content}</p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(message.status)}
                              <span className="text-sm font-medium">
                                {message.error ? 'Query Error' : 'Query Results'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Clock className="w-3 h-3" />
                              <span>{message.executionTime?.toFixed(2)}s</span>
                              <span>•</span>
                              <span>{formatTimestamp(message.timestamp)}</span>
                              {message.rowCount !== undefined && (
                                <>
                                  <span>•</span>
                                  <span>{message.rowCount} rows</span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* SQL Query */}
                          <div className="mb-3">
                            <div className="text-xs font-medium text-gray-600 mb-1">Generated SQL:</div>
                            <SyntaxHighlighter
                              language="sql"
                              style={tomorrow}
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                            >
                              {message.content}
                            </SyntaxHighlighter>
                          </div>

                          {/* Error Message */}
                          {message.error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                              <div className="flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                <span className="text-sm text-red-700">{message.error}</span>
                              </div>
                            </div>
                          )}

                          {/* Query Results Table */}
                          {message.results && message.results.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-gray-600 mb-2">Results:</div>
                              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      {message.columns?.map((column, index) => (
                                        <th
                                          key={index}
                                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          {column}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {message.results.map((row, rowIndex) => (
                                      <tr key={rowIndex} className="hover:bg-gray-50">
                                        {message.columns?.map((column, colIndex) => (
                                          <td
                                            key={colIndex}
                                            className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap"
                                          >
                                            {typeof row[column] === 'object' 
                                              ? JSON.stringify(row[column])
                                              : String(row[column] || '')}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* No Results Message */}
                          {message.results && message.results.length === 0 && !message.error && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <div className="text-sm text-gray-600">No results found for this query.</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                                         <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-2xl px-6 py-4 shadow-sm">
                       <div className="flex items-center space-x-3">
                         <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                         <span className="text-blue-700 font-medium">Generating SQL query...</span>
                       </div>
                     </div>
                  </div>
                )}
                
                {/* Scroll to Bottom Button */}
                {showScrollButton && (
                  <button
                    onClick={scrollToBottom}
                                         className="absolute bottom-6 right-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 hover:shadow-2xl"
                    title="Scroll to bottom"
                  >
                                         <ChevronDown className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Input */}
                             <div className="border-t border-gray-200 p-6 flex-shrink-0 bg-gray-50">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about your mining operations data... (e.g., 'Show me all race logs for CAT 777E-31 today')"
                                             className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none bg-gray-50 text-gray-900 placeholder-gray-500"
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                                         className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    <Send className="w-4 h-4" />
                                         <span className="font-medium">Send</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 